import React from 'react';

import { useTheme } from '../../theme/ThemeContext';
import { formatDateIST } from '../../utils/formatIST';
import { SECTION_STATUS, SECTION_REASON_CODE } from '../../constants/patientCard';

/**
 * PatientCardHeader (EPIC PATIENT-CARD, F5) — demographics header for
 * `/org/patients/:patientId`.
 *
 * Sourced ONLY from the aggregator's `sections.patient` item (the
 * `PatientResponse` body, §1.1) — NEVER from a visit row's `patient_name` /
 * `patient_mobile`, which are a historical write-time snapshot (DEC-6). If a
 * patient is renamed, a visit snapshot silently holds the old name; this
 * header always shows who the patient IS NOW.
 *
 * `status` / `reasonCode` are the `sections.patient` SectionObject's own
 * `status` / `reason_code` (§1.2) — a lane can independently fail even when
 * the envelope call itself succeeded. `NOT_IMPLEMENTED` (transitional — a
 * lane not yet built) and `LANE_READ_FAILED` (a genuine failure) are
 * DISTINCT states and must render differently, never collapsed into one
 * error branch (R1-7).
 */
function initials(firstName, lastName) {
  const a = (firstName || '').trim().charAt(0);
  const b = (lastName || '').trim().charAt(0);
  const s = `${a}${b}`.toUpperCase();
  return s || '?';
}

/** Aadhaar is PII — never render more than the last 4 digits. */
function maskAadhaar(aadhaarNo) {
  const digits = String(aadhaarNo || '').replace(/\D/g, '');
  if (digits.length < 4) return null;
  return `•••• •••• ${digits.slice(-4)}`;
}

function ageGenderLabel(age, gender) {
  if (age && gender) return `${age} yrs / ${gender}`;
  if (age) return `${age} yrs`;
  if (gender) return gender;
  return null;
}

function Chip({ children, tone = 'slate', isDark }) {
  const tones = {
    slate: isDark ? 'bg-slate-800 text-slate-100 border-slate-700' : 'bg-slate-100 text-slate-800 border-slate-300',
    teal: isDark ? 'bg-teal-900/60 text-teal-100 border-teal-700' : 'bg-teal-100 text-teal-800 border-teal-400',
    uhid: isDark ? 'bg-indigo-900/60 text-indigo-100 border-indigo-700' : 'bg-indigo-100 text-indigo-800 border-indigo-400',
  };
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-xs font-semibold ${tones[tone] || tones.slate}`}
    >
      {children}
    </span>
  );
}

export default function PatientCardHeader({ patient, status, reasonCode, onRetry }) {
  const { isDark } = useTheme();

  if (status === SECTION_STATUS.UNAVAILABLE) {
    if (reasonCode === SECTION_REASON_CODE.NOT_IMPLEMENTED) {
      return (
        <div
          data-testid="patient-card-header-not-implemented"
          className={`rounded-lg border px-4 py-3 text-sm ${isDark ? 'bg-slate-800 border-slate-700 text-slate-300' : 'bg-slate-50 border-slate-300 text-slate-600'}`}
        >
          Patient details aren't available on this build yet.
        </div>
      );
    }
    return (
      <div
        data-testid="patient-card-header-unavailable"
        className={`flex items-center justify-between gap-3 rounded-lg border px-4 py-3 text-sm ${isDark ? 'bg-amber-900/30 border-amber-700 text-amber-100' : 'bg-amber-50 border-amber-400 text-amber-900'}`}
      >
        <span>Couldn't load patient details.</span>
        <button
          type="button"
          data-testid="patient-card-retry"
          onClick={onRetry}
          className={`rounded-md border px-3 py-1 text-xs font-semibold ${isDark ? 'bg-slate-800 border-slate-600 text-slate-100 hover:bg-slate-700' : 'bg-white border-slate-400 text-slate-800 hover:bg-slate-100'}`}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!patient) return null;

  const name = `${patient.first_name || ''} ${patient.last_name || ''}`.trim() || 'Unnamed patient';
  const uhidLabel = patient.uhid || '—';
  const registered = formatDateIST(patient.created_at) || '—';
  const ageGender = ageGenderLabel(patient.age, patient.gender);
  const aadhaarMasked = maskAadhaar(patient.aadhaar_no);

  return (
    <div
      data-testid="patient-card-header"
      className={`flex flex-wrap items-start gap-4 rounded-lg border p-4 ${isDark ? 'bg-slate-900 border-slate-700' : 'bg-white border-slate-300'}`}
    >
      <div
        data-testid="patient-card-avatar"
        className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-teal-600 to-teal-800 text-base font-extrabold text-white"
      >
        {initials(patient.first_name, patient.last_name)}
      </div>
      <div className="min-w-[220px] flex-1">
        <p
          data-testid="patient-card-name"
          className={`m-0 text-lg font-extrabold ${isDark ? 'text-slate-50' : 'text-slate-900'}`}
        >
          {name}
        </p>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
          <Chip tone="uhid" isDark={isDark}>
            <span data-testid="patient-card-uhid">{uhidLabel}</span>
          </Chip>
          {ageGender && <Chip isDark={isDark}>{ageGender}</Chip>}
          {patient.mobile && <Chip isDark={isDark}>{'📱'} {patient.mobile}</Chip>}
          <Chip isDark={isDark}>Registered {registered}</Chip>
          {aadhaarMasked && (
            <Chip isDark={isDark}>
              <span data-testid="patient-card-aadhaar">Aadhaar {aadhaarMasked}</span>
            </Chip>
          )}
          <Chip tone="teal" isDark={isDark}>Admin view</Chip>
        </div>
      </div>
    </div>
  );
}
